/**
 * usePilotTop hook.
 */
import { PilotProps as Props, PilotType } from '@shared/types/props/pilot';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { recordingSelector } from '@shared/store/slices/recordings.slice';
import { store } from '@shared/store/store';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useState } from 'react';

/**
 * Hook to manage the pilot top display and content.
 */
export default function usePilotTop(parentProps: Props) {
  // Selectors
  const { currentChannel } = useAppSelector(channelsSelector);
  const { currentRecording } = useAppSelector(recordingSelector);

  // States
  const [isRecording, setIsRecording] = useState(false);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Determines when the recording icon should be displayed.
   */
  useEffect(() => {
    const currentEvent = currentChannel?.getCurrentEvent();
    if (currentEvent) {
      const { recordings } = store.getState();
      const found = recordings.identifiers.find(
        (item) => currentEvent.eventId === item.code
      );
      if (found) {
        setIsRecording(true);
        return;
      }
    }
    setIsRecording(false);
  }, [currentChannel?.id, currentRecording]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const { type } = parentProps;
    return {
      display: {
        dial: type === PilotType.LIVE,
        recording: isRecording,
      },
      text: currentChannel?.dial ?? '',
    };
  }, [currentChannel?.dial, isRecording, parentProps]);
}
